<template>
    <div v-if="!loading" class="create">
        <b-container class="main_layer py-3" fluid>
            <b-container>
                <b-row>
                    <b-col>
                        <p class="fs-36 fw-600 white-color my-2 if-tablet">Внесистемная закупка № {{ purchase.reg_number }}</p>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <p class="fs-14 greyBg-color mb-1">&laquo;{{ $store.state.organization.short_name }}&raquo;</p>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <p class="fs-14 greyBg-color">
                            Пользователь:
                            {{ $store.state.user.full_name }}
                        </p>
                    </b-col>
                </b-row>
            </b-container>
        </b-container>
        <b-container fluid style="background-color: #f5f5f7">
            <b-container class="my-3">
                <online-course-regular-button class="mb-3" />

                <text-header> Информация о закупке</text-header>

                <form-row-edit-textarea v-model="purchase.comment" :rows="5" class="mb-0" label="Примечание к закупке (отображается только в ЛК Заказчика)" />
                <text-row v-model="purchase.order_type" compact label="Тип закупки" />
                <text-row v-if="!isBasedOnFailedPurchase && !isBeforeUncategorizedLimit && !customerCanCreateHiddenPurchase" v-model="purchase.fl44_external_purchase_category" compact label="Категория закупки" />
                <text-row v-if="isBasedOnFailedPurchase" v-model="purchase.failed_fl44_purchase.reg_number" compact label="Номер несостоявшейся закупки" />
                <text-row v-if="!purchase.deliverable_group" v-model="purchase.purchase_object" compact label="Наименование закупки" />
                <text-row v-if="purchase.order_type_limit_used" :value="iskbklimitoptional ? 'Да' : 'Нет'" compact label="Закупка при отсутствии ЛБО (лимитов по КБК)" />
                <text-row v-if="!iskbklimitoptional && purchase.is_advance_report" value="Да" compact label="Авансовый отчет" />
                <text-row v-if="!iskbklimitoptional" v-model="purchase.purchase_limit_year" compact label="Год ПГ (Плана-графика)" />
                <text-row v-if="isHiddenPurchaseCategory" v-model="purchase.purchase_name" compact label="Предмет закупки" />
                <text-row v-else v-model="purchase.deliverable_group" compact label="Предмет закупки" />
                <form-row-edit-text
                    v-if="editablePurchaseObject"
                    v-model="purchase.purchase_object"
                    :v="$v.purchase.purchase_object"
                    class="mb-0"
                    hint="Укажите непосредственно товары, работы, услуги, поставка, выполнение, оказание которых будут являться предметом контракта."
                    label="Объект закупки"
                    placeholder="Введите объект закупки" />
                <text-row v-else v-model="purchase.purchase_object" compact label="Объект закупки" />
                <text-row-date :value="purchase.contract_conclusion_date" compact label="Дата заключения контракта" />
                <text-row-date :value="purchase.contract_execution_date" compact label="Дата исполнения контракта" />
                <form-row-edit-checkbox v-model="purchase.without_vat" label="Поставщик освобождён от уплаты НДС" compact disabled />

                <form-row-specification-table-new
                    id="specification"
                    v-model="purchase.deliverables"
                    :no-add-spec="true"
                    :no-delete-spec="true"
                    :spec-error-message="specErrorMessage"
                    :spec-errors="specErrors"
                    :spec-sum="specSum"
                    :v="$v.purchase.deliverables"
                    :with-count-edit="!isBasedOnFailedPurchase"
                    class="atmo-hash-jump-point"
                    with-price
                    deny-price-edit
                    with-vat-edit
                    :without-vat="purchase.without_vat"
                    vat-readonly />

                <limits-editable-table v-if="!iskbklimitoptional" v-model="purchase.limits" :total-cost="specSum" @validate="limitsValidate" :limit-year="purchase.purchase_limit_year" :order-type="currentOrderType" purchase-type="fl44_external" :purchase-id="purchase.id" />

                <text-header>Информация о поставщике</text-header>
                <template v-if="purchase.is_private_provider">
                    <text-row compact label="Наименование" value="Физическое лицо" />
                    <text-row :value="purchase.private_provider_data.inn" compact label="ИНН" />
                    <text-row :value="purchase.private_provider_data.name" compact label="ФИО" />
                    <text-row :value="purchase.private_provider_data.address" compact label="Адрес регистрации" />
                </template>
                <template v-else>
                    <text-row v-model="purchase.supplier.inn" compact label="ИНН" />
                    <text-row v-if="purchase.supplier.kpp" v-model="purchase.supplier.kpp" compact label="КПП" />
                    <text-row v-model="purchase.supplier.ogrn" compact label="ОГРН / ОГРНИП" />
                    <text-row v-model="purchase.supplier.full_name" compact label="Наименование" />
                    <text-row v-model="purchase.supplier.fact_address" compact label="Фактический адрес" />
                </template>

                <text-header>Информация о контракте</text-header>
                <text-row v-model="purchase.status" compact label="Статус закупки" />
                <form-row-single-line-document-upload v-model="purchase.contract_attachment" :label-cols-lg="3" :v="$v.purchase.contract_attachment" title="Контракт" />
                <form-row-edit-text v-model="purchase.customer_contract_number" :description="Config.get('theme.variables.view.customer_contract_number_description')" :v="$v.purchase.customer_contract_number" label="Номер контракта в системе учета заказчика" />

                <form-row-edit-supplier-accounting v-if="!iskbklimitoptional && !purchase.is_advance_report" v-model="purchase.supplier_accounting" @validate="supplierAccountingValidate">
                    <template #info>
                        <span>Укажите банковские реквизиты поставщика для передачи в РИС.</span>
                    </template>
                </form-row-edit-supplier-accounting>

                <text-header>Документы об исполнении</text-header>
                <p>
                    <strong class="text-danger">Важно!</strong> Редактирование статуса закупки, размещение дополнительных соглашений и документов об исполнении - производите через
                    <router-link :to="{ name: 'ExternalPurchaseShow', params: { purchase: this.$route.params.id }, hash: '#contract-fulfillment' }" style="text-decoration: underline; color: var(--green-color)">форму просмотра</router-link>
                    .
                </p>
                <form-row-documents v-model="purchase.execution_documents" title="" />
                <p v-if="specErrors" class="text-danger" v-text="specErrorMessage"></p>
                <p v-if="limitError" class="text-danger" v-text="limitErrorMessage"></p>

                <b-row class="my-3 mt-5">
                    <b-col>
                        <b-overlay :show="isDataSending" class="d-inline-block" opacity="0.6" rounded spinner-small spinner-variant="primary">
                            <b-button class="mr-2 text-uppercase" variant="custom-outline-secondary" @click="onCancel">Отмена</b-button>
                        </b-overlay>
                        <b-overlay :show="isDataSending" class="d-inline-block" opacity="0.6" rounded spinner-small spinner-variant="primary">
                            <b-button :disabled="!isValid" class="text-uppercase" variant="custom-green" @click="sendData">Сохранить</b-button>
                        </b-overlay>
                    </b-col>
                </b-row>
            </b-container>
        </b-container>
    </div>
</template>

<script>
import { minLength, minValue, required, requiredIf } from 'vuelidate/lib/validators';
import moment from 'moment';
import { mapGetters } from 'vuex';
import Config from '@/rearchitected/core/js/ddriven/application/config/Config';
import roundToTwoDecimals from '@/rearchitected/lib/js/src/misc/roundToTwoDecimals';
import FormRowDocuments from '@/components/common/form-rows/form-row-documents.vue';
import OnlineCourseRegularButton from '@/components/elements/OnlineCourseRegularButton.vue';

export default {
    name: 'ExternalPurchaseEdit',
    metaInfo() {
        return {
            title: 'Редактирование закупки вне ИС ' + this.getRegNumber
        };
    },
    components: {
        OnlineCourseRegularButton,
        FormRowDocuments,
    },
    data() {
        return {
            isDataSending: false,
            iskbklimitoptional: false,
            loading: true,
            editablePurchaseObject: false,
            suggestionOptionsBank: {
                token: process.env.VUE_APP_DADATA_TOKEN,
                type: ['BANK'],
                scrollOnFocus: false,
                triggerSelectOnBlur: true,
                triggerSelectOnEnter: false,
                addon: 'none',
                onSelect: this.onBankSelect,
                onInvalidateSelection: this.onBankInvalidateSelection,
                onSelectNothing: this.onBankSelectNothing,
                noCache: true,
                formatSelected: this.bankFormatSelected
            },
            purchase: {
                reg_number: '',
                comment: '',
                order_type_id: 0,
                order_type: '',
                order_type_limit_used: true,
                fl44_external_purchase_category: '',
                failed_fl44_purchase: null,
                deliverable_group: '',
                deliverable_group_id: null,
                is_advance_report: false,
                purchase_limit_year: 0,
                purchase_name: '',
                purchase_object: '',
                contract_conclusion_date: null,
                contract_execution_date: null,
                without_vat: false,
                deliverables: [],
                price_limit: 0.0,
                limits: [],
                is_private_provider: false,
                supplier: null,
                status: '',
                contract_attachment: null,
                execution_documents: [],
                customer_contract_number: '',
                supplier_accounting: {
                    bank_type: 'bank',
                    bik: null,
                    bank_name: null,
                    bank_city: null,
                    ks: null,
                    rs: null,
                    ls: null,
                    fo_inn: null,
                    fo_name: null
                },
                without_limits: false
            },
            dictionaries: {
                bankTypes: []
            },
            isLimitsValid: false,
            isSupplierAccountingValid: false,
            forFilters: {
                purchaseCategories: []
            }
        };
    },
    validations() {
        return {
            purchase: {
                purchase_object: { required },
                without_vat: { required },
                deliverables: {
                    required,
                    minLength: minLength(1),
                    $each: {
                        ktru_item_id: { required },
                        count: { required, minValue: minValue(0.00001) },
                        price: {
                            required,
                            minValue: minValue(0.01)
                        }
                    }
                },
                contract_attachment: { required },
                customer_contract_number: { required },
                supplier_accounting: {
                    required: requiredIf(() => this.bankRequired),
                    isSupplierAccountingValid() {
                        return !this.bankRequired || this.isSupplierAccountingValid;
                    }
                }
            }
        };
    },
    async mounted() {
        await this.fillData();
    },
    methods: {
        async fillData() {
            const data = await this.$api.dictionary.getList(['bank_types', 'purchase_categories']);
            this.dictionaries.bankTypes = data.bank_types;

            await this.$api.personal.fl44ExternalPurchases
                .getEditData(this.$route.params.id)
                .then((response) => {
                    this.purchase = Object.assign(this.purchase, response);
                    this.editablePurchaseObject = !this.purchase.purchase_object;
                    this.iskbklimitoptional = this.purchase.without_limits;
                })
                .catch((error) => {
                    if (error && error.response && error.response.status === 404) {
                        this.$router.push({ name: '404' }).catch(() => {});
                    }
                });
            this.loading = false;
        },
        prepareForm() {
            const purchase = { ...this.purchase };
            return {
                form: {
                    comment: purchase.comment,
                    purchase_object: purchase.purchase_object,
                    without_vat: purchase.without_vat,
                    deliverables: purchase.deliverables.map((item) => {
                        return {
                            ktru_item_id: item.ktru_item_id,
                            count: item.count,
                            price: item.price,
                            vat: item.vat?.id,
                        }
                    }),
                    limits: purchase.limits.map((item) => {
                        return {
                            id: item.id,
                            year: item.year,
                            amount_assigned: item.amount_assigned
                        };
                    }),
                    start_price: this.specSum,
                    contract_attachment: purchase.contract_attachment,
                    supplier_accounting: purchase.supplier_accounting,
                    customer_contract_number: purchase.customer_contract_number
                }
            };
        },
        async sendData() {
            try {
                this.isDataSending = true;
                if (await this.$api.personal.fl44ExternalPurchases.update(this.prepareForm(), this.$route.params.id)) this.onCancel();
            } finally {
                this.isDataSending = false;
            }
        },
        onCancel() {
            this.$router.push({ name: 'CabinetFl44ContractsList' }).catch(() => {});
        },
        limitsValidate(value) {
            this.isLimitsValid = !value;
        },
        supplierAccountingValidate(value) {
            this.isSupplierAccountingValid = !value;
        }
    },
    computed: {
        Config() {
            return Config;
        },
        bankRequired() {
            return !(this.iskbklimitoptional || this.purchase.is_advance_report);
        },
        features() {
            return Config.get('theme.features');
        },
        spec_total_amount() {
            return roundToTwoDecimals(
                this.$data.purchase.deliverables.reduce((accumulator, spec) => {
                    return (accumulator += roundToTwoDecimals(spec.price * spec.count));
                }, 0)
            );
        },
        ...mapGetters(['getOrganization', 'customerCanCreateHiddenPurchase']),
        isValid() {
            return !this.specErrors && !this.limitError && !this.$v.purchase.$invalid;
        },
        isBasedOnFailedPurchase() {
            return !!this.purchase.failed_fl44_purchase;
        },
        specErrors() {
            return !!this.specErrorMessage;
        },
        limitError() {
            return !!this.limitErrorMessage;
        },
        limitErrorMessage() {
            if (!this.isLimitsValid && !this.$data.iskbklimitoptional) return 'Проверьте правильность заполнения таблицы лимитов';
            return '';
        },
        specErrorMessage() {
            if (this.purchase.deliverables.length === 0) return 'Заполните таблицу спецификаций';
            if (!this.purchase.deliverables.every((e) => e.count > 0)) return 'У одной или нескольких позиций в таблице спецификации не заполнено поле "Количество"';
            if (!this.purchase.deliverables.every((e) => e.price > 0)) return 'У одной или нескольких позиций в таблице спецификации не заполнено поле "Цена за единицу"';
            if (this.specSum > this.purchase.price_limit) return 'Сумма позиций спецификации не может превышать ' + this.$formatPrice(this.purchase.price_limit) + ' руб.';

            if (!this.isBasedOnFailedPurchase) {
                if (!this.purchase.fl44_external_purchase_category) {
                    if (this.isBeforeUncategorizedLimit) {
                        return '';
                    } else {
                        if (this.specSum > 10000) return 'Превышена максимальная допустимая сумма спецификации (' + this.$formatPrice(10000) + ' руб.)';
                    }
                } else {
                    if (this.specSum > this.purchase.price_limit) return 'Превышена максимальная допустимая сумма спецификации (' + this.$formatPrice(this.purchase.price_limit) + ' руб.)';
                }
            } else {
                if (this.specSum > this.purchase.failed_fl44_purchase.start_price) return 'Превышена НЦК несостоявшейся закупки (' + this.$formatPrice(this.purchase.failed_fl44_purchase.start_price) + ' руб.)';
            }
            return '';
        },
        specSum() {
            return roundToTwoDecimals(this.purchase.deliverables.reduce((acc, cv) => acc + roundToTwoDecimals(cv.count * cv.price), 0));
        },
        isBeforeUncategorizedLimit() {
            return !!this.purchase.contract_conclusion_date && moment(this.purchase.contract_conclusion_date).isBefore('2021-02-01');
        },
        isHiddenPurchaseCategory() {
            if (!this.purchase.deliverable_group_id) {
                return false;
            }
            const foundPurchaseCategory = this.forFilters.purchaseCategories.find((i) => i.id === this.purchase.deliverable_group_id);
            return !foundPurchaseCategory;
        },
        currentOrderType() {
            return this.$globalDictionaries.orderTypes.find((item) => item.id === this.purchase.order_type_id);
        },
        getRegNumber() {
            return this.purchase?.reg_number ?? '';
        }
    }
};
</script>

<style scoped>
.badge {
    position: absolute;
    top: -15px;
    right: -15px;
}

.red-star {
    color: red;
}

.main_layer {
    background-image: url('/images/purchases_head.svg');
    background-repeat: no-repeat;
    -webkit-background-size: cover;
    -moz-background-size: cover;
    -o-background-size: cover;
    background-size: cover;
    min-height: 100px;
}

input::-webkit-input-placeholder {
    color: #959ba4 !important;
    opacity: 0.5 !important;
}

input:-moz-placeholder {
    color: #959ba4 !important;
    opacity: 0.5 !important;
}

input::-moz-placeholder {
    color: #959ba4 !important;
    opacity: 0.5 !important;
}

input:-ms-input-placeholder {
    color: #959ba4 !important;
    opacity: 0.5 !important;
}

input::-ms-input-placeholder {
    color: #959ba4 !important;
    opacity: 0.5 !important;
}

input::placeholder {
    color: #959ba4 !important;
    opacity: 0.5 !important;
}

input {
    border-color: rgba(149, 155, 164, 0.2);
}

.block_head-text {
    font-size: 32px;
    color: var(--grey-color);
    font-weight: 600;
    line-height: 48px;
    margin: 33px 0;
}

.table {
    border-collapse: collapse;
}

.table thead th {
    background: #e4e6e8;
    font-size: 14px;
    font-weight: 600;
    padding: 12px;
    color: var(--grey-color);
}

.table tbody td {
    font-size: 14px;
    /*font-weight: 600;*/
    padding: 12px;
    vertical-align: middle;
    border-bottom: 1px solid #dee2e6;
    color: var(--grey-color);
}

.end_list {
    list-style-type: none;
    padding-left: 0px;
    margin-bottom: 40px;
}

.end_list li {
    margin: 15px 0;
    font-size: 14px;
    color: var(--grey-color);
    position: relative;
}

.end_list li::before {
    content: '';
    position: absolute;
    top: 8px;
    left: -15px;
    width: 5px;
    height: 5px;
    -webkit-border-radius: 50%;
    -moz-border-radius: 50%;
    border-radius: 50%;
    background: var(--green-color);
}

.head_register-list {
    color: var(--halfWhite-color);
}

@media screen and (max-width: 769px) {
    .if-tablet {
        font-size: 40px !important;
    }
}
</style>

<style>
.purchase_session {
    position: relative;
}

.purchase_session ul.nav-tabs {
    position: absolute;
    top: -150px;
    width: 100%;
    background: transparent;
    -webkit-box-align: end;
    -webkit-align-items: flex-end;
    -moz-box-align: end;
    -ms-flex-align: end;
    align-items: flex-end;
}

.purchase_session ul.nav-tabs li {
    width: 25%;
}

.purchase_session ul.nav-tabs li a {
    border: none;
}

.purchase_session .nav-link.active {
    border-bottom: 2px solid #fff;
    background: transparent;
}

.purchase_session .nav-link.active .head_register-list {
    color: #fff;
}
</style>

<style lang="less">
@subtext-color: #777;
@preloader-bg-color: rgba(255, 255, 255, 0.75);
@suggestions-bg-color: #fff;
@subtext-label-color: #f5f5f5;

.box-sizing(@sizing: border-box) {
    -ms-box-sizing: @sizing;
    -moz-box-sizing: @sizing;
    -webkit-box-sizing: @sizing;
    box-sizing: @sizing;
}

.rounded(@radius) {
    -webkit-border-radius: @radius;
    -moz-border-radius: @radius;
    border-radius: @radius;
}

.cross-button-mixin() {
    @cross-padding: 20;
    @cross-ray-width: 10;
    @cross-ray-length: 50 - @cross-padding - @cross-ray-width;
    @svg: "<svg width='100' height='100' xmlns='http://www.w3.org/2000/svg'><g><path d='m@{cross-padding},@{cross-padding}m@{cross-ray-width},0l@{cross-ray-length},@{cross-ray-length}l@{cross-ray-length},-@{cross-ray-length}l@{cross-ray-width},@{cross-ray-width}l-@{cross-ray-length},@{cross-ray-length}l@{cross-ray-length},@{cross-ray-length}l-@{cross-ray-width},@{cross-ray-width}l-@{cross-ray-length},-@{cross-ray-length}l-@{cross-ray-length},@{cross-ray-length}l-@{cross-ray-width},-@{cross-ray-width}l@{cross-ray-length},-@{cross-ray-length}l-@{cross-ray-length},-@{cross-ray-length}l@{cross-ray-width},-@{cross-ray-width}' fill='#999'/></g></svg>";
    @svg64: `new Buffer(@{svg}) .toString('base64') `;
    background: transparent url('data:image/svg+xml;base64,@{svg64}') 50% 50% no-repeat;
    background-size: contain;

    cursor: pointer;
    opacity: 0.8;

    &:hover {
        opacity: 1;
    }
}

.suggestions-nowrap {
    white-space: nowrap;
}

/**
   * Основной INPUT
   */
.suggestions-input {
    // IE9 can't determine border-style until it is specified in stylesheets
    //  border: 1px solid grey;
    .box-sizing();
    width: 100%;

    &::-ms-clear {
        display: none;
    }
}

.suggestions-wrapper {
    position: relative;
    margin: 0;
    padding: 0;
    vertical-align: top;

    //Prevent font scaling in landscape while allowing user zoom
    -webkit-text-size-adjust: 100%;
}

.suggestions-addon {
    display: none;
    opacity: 0;
    position: absolute;

    &[data-addon-type='spinner'] {
        background: @preloader-bg-color
            url('data:image/gif;base64,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')
            50% 50% no-repeat;
        .rounded(50%);
    }

    &[data-addon-type='clear'] {
        .cross-button-mixin();
    }
}

/**
   * Выпадающий блок с найденными подсказками
   */
.suggestions-suggestions {
    background: @suggestions-bg-color;
    border: 1px solid #999;
    .box-sizing;
    cursor: default;
    z-index: 9999;

    //Prevent font scaling in landscape while allowing user zoom
    -webkit-text-size-adjust: 100%;

    strong {
        font-weight: normal;
        color: #3399ff;
    }

    &.suggestions-mobile {
        border-style: none;

        .suggestions-suggestion {
            border-bottom: 1px solid #ddd;
        }
    }
}

/**
   * Контейнер для одной подсказки
   */
.suggestions-suggestion {
    padding: 4px 4px;
    overflow: hidden;

    &:hover {
        background: darken(@suggestions-bg-color, 3%);
    }
}

/**
   * Выбранная (активная) подсказка
   */
.suggestions-selected {
    background: darken(@suggestions-bg-color, 6%);

    &:hover {
        background: darken(@suggestions-bg-color, 6%);
    }
}

/**
   * Информационный блок в верхней части выпадашки с подсказками
   */
.suggestions-hint {
    padding: 4px 4px;
    white-space: nowrap;
    overflow: hidden;
    color: @subtext-color;
    font-size: 85%;
    line-height: 20px;
}

.suggestions-constraints {
    list-style: none !important;
    margin: 0 !important;
    padding: 0 !important;
    position: absolute !important;
    white-space: nowrap !important;

    &:empty {
        display: none !important;
    }

    li {
        background: #f8f8f8;
        border: 1px solid #ccc;
        .rounded(3px);
        cursor: default;
        display: inline-block;
        margin: 0 4px 0 0;
        padding: 0 0.5em;

        .suggestions-remove {
            @icon-size: 1em;
            height: @icon-size;
            width: @icon-size;
            display: inline-block;

            margin: 0 0 0 0.25em;
            .cross-button-mixin();
        }

        span {
            vertical-align: middle;
        }
    }
}

/**
   * Дополнительный текст в подсказке, который идет второй строкой
   */
.suggestions-subtext {
    color: @subtext-color;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/**
   * Размещает дополнительный текст в одну строку с основным текстом подсказки
   */
.suggestions-subtext_inline {
    display: inline-block;
    min-width: 6em;
    vertical-align: bottom;
    margin: 0 0.5em 0 0;
}

/**
   * Разделитель нескольких дополнительных текстов
   */
.suggestions-subtext-delimiter {
    display: inline-block;
    width: 2px;
}

/**
   * Выделяет подсказку
   */
.suggestions-subtext_label {
    margin: 0 0 0 0.25em;
    .rounded(3px);
    padding: 0 3px;

    background: @subtext-label-color;
    font-size: 85%;
}

.suggestions-value {
    &[data-suggestion-status='LIQUIDATED'] {
        position: relative;

        &:after {
            position: absolute;
            left: 0;
            right: 0;
            top: 50%;

            border-top: 1px solid rgba(0, 0, 0, 0.4);

            content: '';
        }
    }
}

/**
   * Промо-блок
   */
.suggestions-promo {
    font-size: 85%;
    display: none;
    color: @subtext-color;
    padding: 4px;
    text-align: center;

    a {
        color: @subtext-color;
        display: block;
        filter: grayscale(100%);
        line-height: 20px;
        text-decoration: none;
    }

    a:hover {
        filter: grayscale(0);
    }

    svg {
        height: 20px;
        vertical-align: bottom;
    }
}

.suggestions-promo-desktop {
    position: absolute;
    top: 0;
    right: 0;
    text-align: left;
}
</style>
